import { zipSync, type Zippable } from "fflate";
import { structureProblems, type StudioDoc } from "./model.js";
import { slideFileName } from "./project-file.js";
import { HEIGHT, renderSlide, WIDTH, type RenderAssets } from "./render.js";

/** The export: the PNG slides (01.png, 02.png… cover first) in one ZIP, drawn by the preview's code. */

/** Problems that prevent the export, slide by slide (empty: the export can run). */
export function exportProblems(doc: StudioDoc, assets: RenderAssets): string[] {
  const problems = structureProblems(doc);
  const canvas = document.createElement("canvas");
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext("2d")!;
  doc.slides.forEach((_, i) => {
    for (const p of renderSlide(ctx, doc, i, assets).blocking) problems.push(`Slide ${i + 1} : ${p}`);
  });
  return problems;
}

const toBlob = (canvas: HTMLCanvasElement) =>
  new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Rendu PNG impossible."))), "image/png"));

export interface ExportResult {
  zip: Blob;
  files: Array<{ name: string; bytes: number }>;
  /** Rendering and packing time, in milliseconds. */
  ms: number;
}

export async function exportSlidesZip(doc: StudioDoc, assets: RenderAssets): Promise<ExportResult> {
  const started = performance.now();
  const canvas = document.createElement("canvas");
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext("2d")!;
  const entries: Zippable = {};
  const files: ExportResult["files"] = [];
  for (let i = 0; i < doc.slides.length; i++) {
    const report = renderSlide(ctx, doc, i, assets);
    if (report.blocking.length) throw new Error(`Slide ${i + 1} : ${report.blocking.join(" ")}`);
    const png = new Uint8Array(await (await toBlob(canvas)).arrayBuffer());
    const name = slideFileName(i);
    entries[name] = [png, { level: 0 }]; // PNG is already compressed
    files.push({ name, bytes: png.length });
  }
  const zip = new Blob([zipSync(entries) as Uint8Array<ArrayBuffer>], { type: "application/zip" });
  return { zip, files, ms: performance.now() - started };
}

export function download(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
